import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";

const API_BASE = import.meta.env.VITE_API_BASE
  ?? (globalThis.location?.hostname === "replyradar.miglioresoftware.app"
    ? "https://api-replyradar.miglioresoftware.app"
    : globalThis.location?.hostname === "replyradar.sviluppatoremigliore.com" ? "https://api-replyradar.sviluppatoremigliore.com" : "");

type User = {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  role: string;
  status: string;
  avatarUrl?: string;
  language?: string;
  theme?: string;
  receiveDailySummary?: boolean;
  createdAt?: string;
  approvedAt?: string;
};

type Post = {
  postId: string;
  title: string;
  body?: string;
  url: string;
  subreddit: string;
  status: string;
  relevanceScore: number;
  matchedGroups: string[];
  scannedAt: string;
  publishedUtc?: string;
  generatedReply?: string;
  voteScore?: number;
  commentCount?: number;
  aiScore?: number;
};

type SettingsDto = {
  providers: Array<{ id: string; name: string; provider: string; model: string; apiKey?: string; isDefault?: boolean }>;
  scoringPrompt: string;
  scanIntervalHours: number;
  redditClientId?: string;
  redditClientSecret?: string;
  redditUsername?: string;
  redditPassword?: string;
  incipit: string;
  maxAgeDays: number;
  maxItemsPerFeed: number;
  subreddits: Array<{ name: string; sort: string; minGroups: number; tone: string; searchQuery?: string }>;
  keywordGroups: Array<{ groupName: string; keywords: string[] }>;
  claudeApiKey?: string;
  claudeModel?: string;
};

type Translation = {
  language: string;
  titleTranslated?: string;
  bodyTranslated?: string;
  replyTranslated?: string;
  translatedAt?: string;
};

type Tab = "dashboard" | "history" | "review" | "usage" | "settings" | "users" | "audit";

function routeFromLocation(): { tab: Tab; postId: string | null } {
  const path = globalThis.location?.pathname ?? "/";
  const postMatch = path.match(/^\/post\/([^/]+)/);
  if (postMatch?.[1]) return { tab: "review", postId: decodeURIComponent(postMatch[1]) };
  if (path.startsWith("/history")) return { tab: "history", postId: null };
  if (path.startsWith("/usage")) return { tab: "usage", postId: null };
  if (path.startsWith("/settings")) return { tab: "settings", postId: null };
  if (path.startsWith("/admin/users")) return { tab: "users", postId: null };
  if (path.startsWith("/admin/audit")) return { tab: "audit", postId: null };
  return { tab: "dashboard", postId: null };
}

function tabPath(tab: Tab) {
  const map: Record<Tab, string> = {
    dashboard: "/",
    history: "/history",
    review: "/",
    usage: "/usage",
    settings: "/settings",
    users: "/admin/users",
    audit: "/admin/audit"
  };
  return map[tab];
}

function pushUrl(path: string) {
  if (globalThis.location?.pathname !== path) {
    globalThis.history.pushState(null, "", path);
  }
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const initialRoute = routeFromLocation();
  const [tab, setTab] = useState<Tab>(initialRoute.tab);
  const [theme, setTheme] = useState<"dark" | "light">("dark");
  const [navOpen, setNavOpen] = useState(true);
  const [notice, setNotice] = useState("");
  const [selectedPostId, setSelectedPostId] = useState<string | null>(initialRoute.postId);

  async function refreshUser() {
    setLoading(true);
    const current = await api<User>("/api/v1/users/me", { quiet: true });
    setUser(current);
    setTheme((current?.theme === "light" ? "light" : "dark"));
    setLoading(false);
  }

  useEffect(() => {
    void refreshUser();
    const onPopState = () => {
      const route = routeFromLocation();
      setTab(route.tab);
      setSelectedPostId(route.postId);
    };
    globalThis.addEventListener("popstate", onPopState);
    return () => globalThis.removeEventListener("popstate", onPopState);
  }, []);

  function navigate(tabId: Tab) {
    pushUrl(tabPath(tabId));
    setTab(tabId);
    if (tabId !== "review") setSelectedPostId(null);
  }

  function openPost(postId: string) {
    pushUrl(`/post/${encodeURIComponent(postId)}`);
    setSelectedPostId(postId);
    setTab("review");
  }

  if (loading) {
    return <PublicFrame theme={theme}><div className="pending-state">Caricamento sessione...</div></PublicFrame>;
  }

  if (!user) {
    return <Login theme={theme} onDone={async () => { globalThis.history.replaceState(null, "", "/"); setTab("dashboard"); await refreshUser(); }} />;
  }

  return (
    <AppFrame user={user} tab={tab} setTab={navigate} theme={theme} setTheme={setTheme} navOpen={navOpen} setNavOpen={setNavOpen}>
      {notice && <div className="scan-message">{notice}</div>}
      {tab === "dashboard" && <Dashboard setNotice={setNotice} onOpen={openPost} />}
      {tab === "history" && <HistoryView onOpen={openPost} />}
      {tab === "review" && <PostReview postId={selectedPostId} setNotice={setNotice} onBack={() => navigate("dashboard")} />}
      {tab === "usage" && <UsageView />}
      {tab === "settings" && <SettingsView setNotice={setNotice} />}
      {tab === "users" && <UsersView />}
      {tab === "audit" && <AuditView />}
    </AppFrame>
  );
}

function PublicFrame({ theme, children }: { theme: "dark" | "light"; children: React.ReactNode }) {
  return (
    <div className={`rr-layout theme-${theme}`}>
      <main className="rr-main rr-main-public">{children}</main>
    </div>
  );
}

function AppFrame(props: {
  user: User;
  tab: Tab;
  setTab: (tab: Tab) => void;
  theme: "dark" | "light";
  setTheme: (theme: "dark" | "light") => void;
  navOpen: boolean;
  setNavOpen: (value: boolean) => void;
  children: React.ReactNode;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const displayName = props.user.fullName?.trim() || `${props.user.firstName ?? ""} ${props.user.lastName ?? ""}`.trim() || props.user.email;
  const avatarUrl = props.user.avatarUrl || "https://www.gravatar.com/avatar?d=identicon&s=80";
  const nav: Array<[Tab, string, string]> = [
    ["dashboard", "Dashboard", "▣"],
    ["history", "History", "☰"],
    ["usage", "Usage", "●"],
    ["settings", "Settings", "⊕"],
    ["users", "Users", "◎"],
    ["audit", "Audit log", "⊙"]
  ];

  return (
    <div className={`rr-layout theme-${props.theme}`}>
      <header className="rr-appbar">
        <div className="rr-appbar-left">
          <button className={`hamburger ${props.navOpen ? "open" : ""}`} onClick={() => props.setNavOpen(!props.navOpen)} aria-label="Menu">
            <span></span><span></span><span></span>
          </button>
          <a href="/" className="rr-brand" onClick={(event) => { event.preventDefault(); props.setTab("dashboard"); }}>Reply<span>Radar</span></a>
        </div>
        <div className="rr-appbar-right">
          <button className="theme-toggle-btn" title="Cambia tema" aria-label="Cambia tema" onClick={() => props.setTheme(props.theme === "dark" ? "light" : "dark")}>
            <span>{props.theme === "dark" ? "☀" : "☾"}</span>
          </button>
          <div className="rr-user">
            <button className="user-btn" onClick={() => setMenuOpen(!menuOpen)}>
              <Avatar className="user-avatar" src={avatarUrl} label={displayName} />
              <span className="user-meta"><strong>{displayName}</strong></span>
              <span className={`chevron ${menuOpen ? "up" : ""}`}>▾</span>
            </button>
            {menuOpen && (
              <div className="user-dropdown">
                <div className="dropdown-section-label">Lingua</div>
                <button className="dropdown-item lang-item active">Italiano</button>
                <button className="dropdown-item lang-item">English</button>
                <button className="dropdown-item lang-item">Español</button>
                <div className="dropdown-divider"></div>
                <a href={`${API_BASE}/auth/logout`} className="dropdown-item logout-item">Esci</a>
              </div>
            )}
          </div>
        </div>
      </header>
      <div className="rr-body">
        {props.navOpen && <div className="sidebar-overlay" onClick={() => props.setNavOpen(false)}></div>}
        <aside className={`rr-sidebar ${props.navOpen ? "open" : ""}`}>
          <nav className="sidebar-nav">
            {nav.map(([id, label, icon]) => (
              <button key={id} className={`nav-link ${props.tab === id ? "active" : ""}`} onClick={() => props.setTab(id)}>
                <span className="nav-icon">{icon}</span>
                <span className="nav-label">{label}</span>
              </button>
            ))}
          </nav>
        </aside>
        <main className="rr-main">{props.children}</main>
      </div>
    </div>
  );
}

function Avatar({ src, label, className }: { src?: string; label: string; className?: string }) {
  const [failed, setFailed] = useState(false);
  const letters = label.split(/\s|@/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("");
  if (!src || failed) {
    return <span className={className}>{letters || "RR"}</span>;
  }
  return <img className={className} src={src} alt={label} referrerPolicy="no-referrer" onError={() => setFailed(true)} />;
}

function Login({ theme, onDone }: { theme: "dark" | "light"; onDone: () => void }) {
  const params = new URLSearchParams(globalThis.location?.search ?? "");
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [code, setCode] = useState(params.get("code") ?? "");
  const [step, setStep] = useState<"request" | "verify">(globalThis.location?.pathname === "/verify" ? "verify" : "request");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function requestMagic(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    const result = await api<{ success: boolean; error?: string }>("/auth/request-magic", {
      method: "POST",
      body: JSON.stringify({ email: email.trim() }),
      quiet: true
    });
    setBusy(false);
    if (result?.success) {
      setStep("verify");
      setMessage("Ti abbiamo inviato un codice via email.");
      globalThis.history.pushState(null, "", `/verify?email=${encodeURIComponent(email.trim())}`);
      return;
    }
    setError(result?.error || "Account in attesa di approvazione o non abilitato.");
  }

  async function verifyMagic(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const result = await api<{ success: boolean; error?: string }>("/auth/verify-code-json", {
      method: "POST",
      body: JSON.stringify({ email: email.trim(), code: code.trim() }),
      quiet: true
    });
    setBusy(false);
    if (result?.success) {
      onDone();
      return;
    }
    setError(result?.error || "Codice non valido.");
  }

  return (
    <PublicFrame theme={theme}>
      <div className="auth-wrapper">
        <div className="auth-card glass">
          <div className="auth-logo">
            <div className="logo-text">Reply<span className="logo-accent">Radar</span></div>
          </div>
          {step === "request" ? (
            <form className="auth-form" onSubmit={requestMagic}>
              <div className="form-group">
                <label>Email</label>
                <input className="form-input" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="nome@azienda.com" autoComplete="email" required />
              </div>
              <button type="submit" className="btn-auth" disabled={busy}>{busy ? <span className="spinner"></span> : null} Richiedi codice</button>
            </form>
          ) : (
            <form className="auth-form" onSubmit={verifyMagic}>
              <div className="verify-email">{email}</div>
              <div className="form-group">
                <label>Codice</label>
                <input className="form-input code-input" inputMode="numeric" value={code} onChange={(event) => setCode(event.target.value)} placeholder="000000" autoComplete="one-time-code" required />
              </div>
              <button type="submit" className="btn-auth" disabled={busy}>{busy ? <span className="spinner"></span> : null} Entra</button>
              <button type="button" className="verify-back" onClick={() => setStep("request")}>Cambia email</button>
            </form>
          )}
          {message && <div className="pending-state">{message}</div>}
          {error && <div className="settings-warning settings-alert">{error}</div>}
        </div>
      </div>
    </PublicFrame>
  );
}

function Dashboard({ setNotice, onOpen }: { setNotice: (value: string) => void; onOpen: (postId: string) => void }) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [status, setStatus] = useState("");
  const [subreddit, setSubreddit] = useState("");
  const [busy, setBusy] = useState(false);

  async function loadPosts() {
    const query = new URLSearchParams({ page: "1", pageSize: "50" });
    if (status) query.set("status", status);
    if (subreddit) query.set("subreddit", subreddit);
    const result = await api<{ posts: Post[] }>(`/api/v1/posts?${query}`);
    setPosts(result?.posts ?? []);
  }

  useEffect(() => {
    void loadPosts();
  }, [status, subreddit]);

  const stats = useMemo(() => ({
    newCount: posts.filter((p) => p.status === "New").length,
    generated: posts.filter((p) => p.status === "Generated" || p.generatedReply).length,
    replied: posts.filter((p) => p.status === "Replied").length,
    total: posts.length
  }), [posts]);

  async function scan() {
    setBusy(true);
    const result = await api<{ message?: string; newPosts?: number }>("/api/v1/posts/scan", { method: "POST", quiet: true });
    setBusy(false);
    setNotice(result?.message || `Scan completata. Nuovi post: ${result?.newPosts ?? 0}`);
    await loadPosts();
  }

  return (
    <>
      <header className="rr-header">
        <h1>Dashboard</h1>
        <button className="btn-scan" onClick={scan} disabled={busy}>{busy ? <span className="spinner"></span> : null} Scan</button>
      </header>
      <div className="stats-row">
        <Stat label="Nuovi" value={stats.newCount} />
        <Stat label="Generate" value={stats.generated} />
        <Stat label="Risposte" value={stats.replied} />
        <Stat label="Totali" value={stats.total} />
      </div>
      <div className="filters">
        <select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="">Tutti gli stati</option>
          <option value="New">New</option>
          <option value="Generated">Generated</option>
          <option value="Approved">Approved</option>
          <option value="Replied">Replied</option>
          <option value="Ignored">Ignored</option>
        </select>
        <input className="form-input" value={subreddit} onChange={(event) => setSubreddit(event.target.value)} placeholder="Filtra subreddit" />
      </div>
      <div className="post-list">
        {posts.map((post) => <PostCard key={post.postId} post={post} onRefresh={loadPosts} onOpen={onOpen} />)}
        {posts.length === 0 && <div className="empty-state glass-card">Nessun post trovato.</div>}
      </div>
    </>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return <div className="stat-item"><span className="stat-value">{value}</span><span className="stat-label">{label}</span></div>;
}

function PostCard({ post, onRefresh, onOpen }: { post: Post; onRefresh: () => void; onOpen?: (postId: string) => void }) {
  async function updateStatus(nextStatus: string) {
    await api(`/api/v1/posts/${post.postId}/status`, { method: "PUT", body: JSON.stringify({ status: nextStatus }) });
    await onRefresh();
  }

  async function generateReply() {
    await api(`/api/v1/posts/${post.postId}/generate-reply`, { method: "POST" });
    await onRefresh();
  }

  return (
    <article className={`post-card ${post.status.toLowerCase()}`}>
      <div className="post-card-inner">
        <div className="post-meta">
          <span className="subreddit-pill">r/{post.subreddit}</span>
          <span className={`status-badge ${post.status.toLowerCase()}`}>{post.status}</span>
          <span className="score-chip">score {post.relevanceScore}</span>
          {post.voteScore !== undefined && <span className="vote-chip">▲ {post.voteScore}</span>}
        </div>
        <h2 className="post-title">
          <a href={`/post/${post.postId}`} onClick={(event) => { if (onOpen) { event.preventDefault(); onOpen(post.postId); } }}>{post.title}</a>
        </h2>
        {post.body && <div className="post-body-snip">{post.body.slice(0, 360)}</div>}
        {post.generatedReply && <div className="reply-preview">{post.generatedReply}</div>}
        <div className="post-bottom">
          <div className="post-groups">
            {(post.matchedGroups?.length ? post.matchedGroups : ["nessun gruppo"]).map((group) => <span className="group-badge" key={group}>{group}</span>)}
          </div>
          <div className="post-actions">
            {onOpen && <button className="btn-review" onClick={() => onOpen(post.postId)}>{post.status === "New" ? "Genera" : "Rivedi"}</button>}
            <button className="btn-generate" onClick={generateReply}>Genera</button>
            <button className="btn-replied" onClick={() => updateStatus("Replied")}>Risposto</button>
            <button className="btn-skip-quick" onClick={() => updateStatus("Ignored")}>Salta</button>
          </div>
        </div>
      </div>
    </article>
  );
}

function HistoryView({ onOpen }: { onOpen: (postId: string) => void }) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api<{ posts: Post[] }>("/api/v1/posts?page=1&pageSize=100").then((result) => setPosts(result?.posts ?? []));
  }, []);

  const filtered = posts.filter((post) => `${post.title} ${post.subreddit} ${post.generatedReply ?? ""}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <header className="rr-header"><h1>History</h1><span className="counter-badge">{filtered.length}</span></header>
      <div className="search-bar"><input className="search-input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Cerca nello storico" /></div>
      <div className="history-list">{filtered.map((post) => <PostCard key={post.postId} post={post} onRefresh={() => {}} onOpen={onOpen} />)}</div>
    </>
  );
}

function PostReview({ postId, setNotice, onBack }: { postId: string | null; setNotice: (value: string) => void; onBack: () => void }) {
  const [post, setPost] = useState<Post | null>(null);
  const [reply, setReply] = useState("");
  const [variants, setVariants] = useState<string[]>([]);
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [activeLanguage, setActiveLanguage] = useState("original");
  const [busy, setBusy] = useState("");

  async function load() {
    if (!postId) return;
    const nextPost = await api<Post>(`/api/v1/posts/${postId}`);
    setPost(nextPost);
    setReply(nextPost?.generatedReply ?? "");
    const nextTranslations = await api<Translation[]>(`/api/v1/posts/${postId}/translations`);
    setTranslations(nextTranslations ?? []);
  }

  useEffect(() => {
    void load();
  }, [postId]);

  if (!postId) return <div className="empty">Seleziona un post dalla dashboard.</div>;
  if (!post) return <div className="pending-state">Caricamento post...</div>;

  const currentPost = post;
  const activeTranslation = translations.find((item) => item.language === activeLanguage);
  const title = activeLanguage === "original" ? currentPost.title : activeTranslation?.titleTranslated || currentPost.title;
  const body = activeLanguage === "original" ? currentPost.body : activeTranslation?.bodyTranslated || currentPost.body;
  const translatedReply = activeLanguage === "original" ? reply : activeTranslation?.replyTranslated || reply;

  async function generateReply() {
    setBusy("reply");
    const result = await api<{ reply?: string; error?: string }>(`/api/v1/posts/${currentPost.postId}/generate-reply`, { method: "POST" });
    setBusy("");
    setReply(result?.reply ?? reply);
    setNotice(result?.error ? `Risposta generata con fallback: ${result.error}` : "Risposta generata.");
    await load();
  }

  async function generateVariants() {
    setBusy("variants");
    const result = await api<string[]>(`/api/v1/posts/${currentPost.postId}/generate-variants`, { method: "POST" });
    setBusy("");
    setVariants(result ?? []);
  }

  async function translate(kind: "content" | "reply") {
    setBusy(kind);
    await api(`/api/v1/posts/${currentPost.postId}/${kind === "content" ? "translate-content" : "translate-reply"}`, { method: "POST" });
    setBusy("");
    setNotice(kind === "content" ? "Contenuto tradotto." : "Risposta tradotta.");
    await load();
    setActiveLanguage("it");
  }

  async function updateStatus(status: string) {
    await api(`/api/v1/posts/${currentPost.postId}/status`, { method: "PUT", body: JSON.stringify({ status, reply }) });
    setNotice(`Post aggiornato: ${status}.`);
    await load();
  }

  return (
    <div className="review-container">
      <button className="back-link" onClick={onBack}>← Dashboard</button>
      <section className="post-header">
        <div className="post-meta">
          <span className="subreddit-pill">r/{post.subreddit}</span>
          <span className="age-chip">{new Date(post.publishedUtc ?? post.scannedAt).toLocaleString()}</span>
          <span className="score-chip">⚡ {post.relevanceScore}</span>
          <span className={`status-badge ${post.status.toLowerCase()}`}>{post.status}</span>
          <a className="thread-link-badge" href={post.url} target="_blank" rel="noreferrer">↗</a>
        </div>
        <h2>{title}</h2>
        <div className="groups">
          {post.matchedGroups.map((group) => <span className="group-badge" key={group}>{group}</span>)}
        </div>
      </section>

      <section className="glass-card">
        <div className="section-header">
          <span className="section-title">Contenuto</span>
          <div className="lang-tabs">
            <button className={`lang-tab ${activeLanguage === "original" ? "active" : ""}`} onClick={() => setActiveLanguage("original")}>Originale</button>
            {translations.map((item) => <button key={item.language} className={`lang-tab ${activeLanguage === item.language ? "active" : ""}`} onClick={() => setActiveLanguage(item.language)}>{item.language.toUpperCase()}</button>)}
            <button className="lang-tab add-lang" onClick={() => translate("content")} disabled={busy === "content"}>{busy === "content" ? "..." : "Traduci"}</button>
          </div>
        </div>
        <div className="post-body-scroll">
          <div className="content-block">
            <span className="content-label">Titolo</span>
            <div className="post-title-text">{title}</div>
          </div>
          <div className="content-block">
            <span className="content-label">Testo</span>
            <div className="post-body-text">{body || "Link post - nessun testo"}</div>
          </div>
        </div>
      </section>

      <section className="reply-section">
        <div className="section-header">
          <span className="section-title">Risposta</span>
          <div className="reply-toolbar">
            <button className="btn-generate" onClick={generateReply} disabled={busy === "reply"}>{busy === "reply" ? <span className="spinner"></span> : null} Genera</button>
            <button className="btn-variants" onClick={generateVariants} disabled={busy === "variants"}>Varianti</button>
            <button className="btn-copy" onClick={() => navigator.clipboard.writeText(reply)}>Copia</button>
            <button className="btn-open" onClick={() => translate("reply")} disabled={busy === "reply"}>Traduci reply</button>
          </div>
        </div>
        {variants.length > 0 && (
          <div className="variants-list">
            {variants.map((variant, index) => (
              <button className={`variant-card ${variant === reply ? "selected" : ""}`} key={`${variant}-${index}`} onClick={() => setReply(variant)}>
                <div className="variant-label">Variante {index + 1}</div>
                <div className="variant-text">{variant}</div>
              </button>
            ))}
          </div>
        )}
        {activeLanguage === "original" ? (
          <textarea className="reply-editor" value={reply} onChange={(event) => setReply(event.target.value)} rows={10} />
        ) : (
          <div className="reply-translated-box">{translatedReply || "Nessuna traduzione disponibile."}</div>
        )}
      </section>

      <div className="status-actions">
        <button className="btn-replied" onClick={() => updateStatus("Replied")}>Segna risposto</button>
        <button className="btn-review" onClick={() => updateStatus("Approved")}>Approva</button>
        <button className="btn-skip" onClick={() => updateStatus("Ignored")}>Salta</button>
      </div>
    </div>
  );
}

function SettingsView({ setNotice }: { setNotice: (value: string) => void }) {
  const [settings, setSettings] = useState<SettingsDto | null>(null);
  const [section, setSection] = useState<"general" | "subreddits" | "keywords" | "prompts">(() => settingsSectionFromPath());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api<SettingsDto>("/api/v1/settings").then(setSettings);
    const onPopState = () => setSection(settingsSectionFromPath());
    globalThis.addEventListener("popstate", onPopState);
    return () => globalThis.removeEventListener("popstate", onPopState);
  }, []);

  if (!settings) return <div className="pending-state">Caricamento impostazioni...</div>;
  const currentSettings = settings;

  function navigateSection(next: typeof section) {
    const path = next === "general" ? "/settings" : `/settings/${next}`;
    pushUrl(path);
    setSection(next);
  }

  function patch(next: Partial<SettingsDto>) {
    setSettings({ ...currentSettings, ...next });
  }

  function updateProvider(index: number, provider: SettingsDto["providers"][number]) {
    const providers = [...currentSettings.providers];
    providers[index] = provider;
    patch({ providers });
  }

  function addProvider() {
    patch({
      providers: [
        ...currentSettings.providers,
        { id: crypto.randomUUID().slice(0, 8), name: "Nuovo provider", provider: "claude", model: "claude-sonnet-4-6", apiKey: "", isDefault: currentSettings.providers.length === 0 }
      ]
    });
  }

  function setDefaultProvider(index: number) {
    patch({ providers: currentSettings.providers.map((provider, itemIndex) => ({ ...provider, isDefault: itemIndex === index })) });
  }

  function removeProvider(index: number) {
    const providers = currentSettings.providers.filter((_, itemIndex) => itemIndex !== index);
    if (providers.length && !providers.some((provider) => provider.isDefault)) providers[0] = { ...providers[0]!, isDefault: true };
    patch({ providers });
  }

  function updateSubreddit(index: number, subreddit: SettingsDto["subreddits"][number]) {
    const subreddits = [...currentSettings.subreddits];
    subreddits[index] = subreddit;
    patch({ subreddits });
  }

  function updateKeywordGroup(index: number, group: SettingsDto["keywordGroups"][number]) {
    const keywordGroups = [...currentSettings.keywordGroups];
    keywordGroups[index] = group;
    patch({ keywordGroups });
  }

  async function save() {
    setSaving(true);
    const normalized: SettingsDto = {
      ...currentSettings,
      providers: currentSettings.providers.length ? currentSettings.providers.map((provider, index) => ({
        ...provider,
        name: provider.name.trim() || `Provider ${index + 1}`,
        provider: provider.provider || "claude",
        model: provider.model || modelOptions(provider.provider)[0] || "claude-sonnet-4-6",
        isDefault: currentSettings.providers.some((item) => item.isDefault) ? Boolean(provider.isDefault) : index === 0
      })) : [{ id: "default", name: "Provider principale", provider: "claude", model: "claude-sonnet-4-6", apiKey: "", isDefault: true }],
      subreddits: currentSettings.subreddits
        .filter((subreddit) => subreddit.name.trim())
        .map((subreddit) => ({
          name: subreddit.name.trim().replace(/^r\//, ""),
          sort: subreddit.sort || "new",
          minGroups: Math.max(1, Number(subreddit.minGroups || 1)),
          tone: subreddit.tone || "standard",
          searchQuery: subreddit.searchQuery?.trim() ?? ""
        })),
      keywordGroups: currentSettings.keywordGroups
        .filter((group) => group.groupName.trim())
        .map((group) => ({ groupName: group.groupName.trim(), keywords: group.keywords.map((keyword) => keyword.trim()).filter(Boolean) }))
    };
    await api("/api/v1/settings", { method: "PUT", body: JSON.stringify(normalized) });
    setSettings(normalized);
    setSaving(false);
    setNotice("Impostazioni salvate.");
  }

  return (
    <>
      <header className="rr-header"><h1>Settings</h1></header>
      <div className="settings-tabs">
        <button className={`settings-tab ${section === "general" ? "active" : ""}`} onClick={() => navigateSection("general")}>Account</button>
        <button className={`settings-tab ${section === "subreddits" ? "active" : ""}`} onClick={() => navigateSection("subreddits")}>Subreddits</button>
        <button className={`settings-tab ${section === "keywords" ? "active" : ""}`} onClick={() => navigateSection("keywords")}>Keywords</button>
        <button className={`settings-tab ${section === "prompts" ? "active" : ""}`} onClick={() => navigateSection("prompts")}>Prompts</button>
      </div>

      {section === "general" && (
        <>
          <section className="settings-card">
            <div className="settings-card-header">
              <div className="settings-card-icon">AI</div>
              <div><h2>Provider AI</h2><p className="settings-card-desc">Provider e modello usati per generare le risposte.</p></div>
            </div>
            {(settings.providers.length ? settings.providers : [{ id: "default", name: "Provider principale", provider: "claude", model: "claude-sonnet-4-6", apiKey: "", isDefault: true }]).map((provider, index) => (
              <div className={`provider-item ${provider.isDefault ? "provider-default" : ""}`} key={provider.id || index}>
                <div className="provider-name-row">
                  <input className="form-input provider-name-input" value={provider.name} onChange={(event) => updateProvider(index, { ...provider, name: event.target.value })} placeholder="Nome provider" />
                  {provider.isDefault ? <span className="default-badge">Default</span> : <button className="btn-set-default" onClick={() => setDefaultProvider(index)}>Imposta default</button>}
                  {settings.providers.length > 1 && <button className="btn-remove-provider" onClick={() => removeProvider(index)}>x</button>}
                </div>
                <div className="provider-item-fields">
                  <div className="setting-row">
                    <label>Provider</label>
                    <select className="form-input provider-select" value={provider.provider} onChange={(event) => updateProvider(index, { ...provider, provider: event.target.value, model: modelOptions(event.target.value)[0] || "claude-sonnet-4-6" })}>
                      <option value="claude">Anthropic (Claude)</option>
                      <option value="openai">OpenAI</option>
                      <option value="openrouter">OpenRouter</option>
                    </select>
                  </div>
                  <div className="setting-row">
                    <label>Modello</label>
                    <select className="form-input provider-select" value={provider.model} onChange={(event) => updateProvider(index, { ...provider, model: event.target.value })}>
                      {modelOptions(provider.provider).map((model) => <option value={model} key={model}>{model}</option>)}
                    </select>
                  </div>
                  <div className="setting-row">
                    <label>API key</label>
                    <input className="form-input" type="password" value={provider.apiKey ?? ""} onChange={(event) => updateProvider(index, { ...provider, apiKey: event.target.value })} placeholder="sk-..." />
                  </div>
                </div>
              </div>
            ))}
            <button className="btn-add-provider" onClick={addProvider}>+ Aggiungi provider</button>
          </section>

          <section className="settings-card">
            <div className="settings-card-header">
              <div className="settings-card-icon">Scan</div>
              <div><h2>Scan automatico</h2><p className="settings-card-desc">Limiti e intervallo del radar.</p></div>
            </div>
            <div className="setting-row"><label>Intervallo ore</label><input className="form-input narrow" type="number" min="1" max="168" value={settings.scanIntervalHours} onChange={(e) => patch({ scanIntervalHours: Number(e.target.value) })} /></div>
            <div className="setting-row"><label>Max age days</label><input className="form-input narrow" type="number" min="1" value={settings.maxAgeDays} onChange={(e) => patch({ maxAgeDays: Number(e.target.value) })} /></div>
            <div className="setting-row"><label>Max items</label><input className="form-input narrow" type="number" min="1" value={settings.maxItemsPerFeed} onChange={(e) => patch({ maxItemsPerFeed: Number(e.target.value) })} /></div>
          </section>

          <section className="settings-card">
            <div className="settings-card-header">
              <div className="settings-card-icon">OAuth</div>
              <div><h2>Reddit API OAuth</h2><p className="settings-card-desc">Credenziali Reddit per arricchire thread e metriche.</p></div>
            </div>
            <div className="reddit-oauth-grid">
              <div className="setting-row"><label>Client ID</label><input className="form-input" value={settings.redditClientId ?? ""} onChange={(event) => patch({ redditClientId: event.target.value })} /></div>
              <div className="setting-row"><label>Client secret</label><input className="form-input" type="password" value={settings.redditClientSecret ?? ""} onChange={(event) => patch({ redditClientSecret: event.target.value })} /></div>
              <div className="setting-row"><label>Username Reddit</label><input className="form-input" value={settings.redditUsername ?? ""} onChange={(event) => patch({ redditUsername: event.target.value })} /></div>
              <div className="setting-row"><label>Password Reddit</label><input className="form-input" type="password" value={settings.redditPassword ?? ""} onChange={(event) => patch({ redditPassword: event.target.value })} /></div>
            </div>
          </section>
        </>
      )}

      {section === "subreddits" && (
        <section className="settings-card">
          <div className="settings-card-header">
            <div className="settings-card-icon">r/</div>
            <div><h2>Subreddits</h2><p className="settings-card-desc">Feed monitorati, sort, query e tono per ogni subreddit.</p></div>
          </div>
          <div className="subreddit-cards">
            {settings.subreddits.map((subreddit, index) => (
              <div className="sub-card glass-card" key={`${subreddit.name}-${index}`}>
                <div className="sub-card-top">
                  <div><div className="sub-card-title">r/{subreddit.name || "nuovo"}</div><div className="sub-card-desc">{subreddit.searchQuery ? `Ricerca: ${subreddit.searchQuery}` : "Feed standard"}</div></div>
                  <button className="btn-remove" onClick={() => patch({ subreddits: settings.subreddits.filter((_, itemIndex) => itemIndex !== index) })}>x</button>
                </div>
                <div className="sub-card-grid">
                  <div className="sub-field"><label className="sub-label">Nome subreddit</label><input className="form-input" value={subreddit.name} onChange={(event) => updateSubreddit(index, { ...subreddit, name: event.target.value })} /></div>
                  <div className="sub-field"><label className="sub-label">Ricerca Reddit</label><input className="form-input" value={subreddit.searchQuery ?? ""} onChange={(event) => updateSubreddit(index, { ...subreddit, searchQuery: event.target.value })} /></div>
                  <div className="sub-field"><label className="sub-label">Sort</label><select className="form-input" value={subreddit.sort} onChange={(event) => updateSubreddit(index, { ...subreddit, sort: event.target.value })}><option value="new">new</option><option value="hot">hot</option><option value="top">top</option><option value="rising">rising</option></select></div>
                  <div className="sub-field"><label className="sub-label">Gruppi minimi</label><input className="form-input" type="number" min="1" max="5" value={subreddit.minGroups} onChange={(event) => updateSubreddit(index, { ...subreddit, minGroups: Number(event.target.value) })} /></div>
                  <div className="sub-field"><label className="sub-label">Tone</label><select className="form-input" value={subreddit.tone} onChange={(event) => updateSubreddit(index, { ...subreddit, tone: event.target.value })}><option value="standard">standard</option><option value="technical">technical</option><option value="educational">educational</option></select></div>
                </div>
              </div>
            ))}
          </div>
          <button className="btn-add" onClick={() => patch({ subreddits: [...settings.subreddits, { name: "", sort: "new", minGroups: 1, tone: "standard", searchQuery: "" }] })}>Aggiungi subreddit</button>
        </section>
      )}

      {section === "keywords" && (
        <section className="settings-card">
          <div className="settings-card-header">
            <div className="settings-card-icon">KW</div>
            <div><h2>Keywords</h2><p className="settings-card-desc">Gruppi keyword usati per matching e rilevanza.</p></div>
          </div>
          <div className="keyword-cards">
            {settings.keywordGroups.map((group, index) => (
              <div className="keyword-card glass-card" key={`${group.groupName}-${index}`}>
                <div className="keyword-card-top">
                  <div><div className="keyword-card-title">{group.groupName || "Nuovo gruppo"}</div><div className="keyword-card-desc">{group.keywords.length} keyword attive</div></div>
                  <button className="btn-remove" onClick={() => patch({ keywordGroups: settings.keywordGroups.filter((_, itemIndex) => itemIndex !== index) })}>x</button>
                </div>
                <div className="keyword-card-grid">
                  <div className="keyword-field"><label className="keyword-label">Nome gruppo</label><input className="form-input" value={group.groupName} onChange={(event) => updateKeywordGroup(index, { ...group, groupName: event.target.value })} /></div>
                  <div className="keyword-field keyword-field-wide"><label className="keyword-label">Keyword</label><input className="form-input" value={group.keywords.join(", ")} onChange={(event) => updateKeywordGroup(index, { ...group, keywords: event.target.value.split(",").map((item) => item.trim()).filter(Boolean) })} /></div>
                </div>
                <div className="keyword-chip-row">{group.keywords.map((keyword) => <span className="keyword-chip" key={keyword}>{keyword}</span>)}</div>
              </div>
            ))}
          </div>
          <button className="btn-add" onClick={() => patch({ keywordGroups: [...settings.keywordGroups, { groupName: "", keywords: [] }] })}>Aggiungi gruppo</button>
        </section>
      )}

      {section === "prompts" && (
        <section className="settings-card">
          <div className="settings-card-header">
            <div className="settings-card-icon">Score</div>
            <div><h2>Prompt</h2><p className="settings-card-desc">Istruzioni per scoring e incipit della risposta.</p></div>
          </div>
          <div className="setting-row"><label>Incipit</label><textarea className="form-input" value={settings.incipit} onChange={(e) => patch({ incipit: e.target.value })} /></div>
          <div className="setting-row"><label>Scoring prompt</label><textarea className="form-input scoring-prompt-textarea" value={settings.scoringPrompt} onChange={(e) => patch({ scoringPrompt: e.target.value })} /></div>
        </section>
      )}

      <div className="settings-actions"><button className="btn-save" onClick={save} disabled={saving}>{saving ? <span className="spinner"></span> : null} Salva</button></div>
    </>
  );
}

function settingsSectionFromPath(): "general" | "subreddits" | "keywords" | "prompts" {
  const path = globalThis.location?.pathname ?? "/settings";
  if (path.includes("/settings/subreddits")) return "subreddits";
  if (path.includes("/settings/keywords")) return "keywords";
  if (path.includes("/settings/prompts")) return "prompts";
  return "general";
}

function modelOptions(provider: string) {
  if (provider === "openai") return ["gpt-4.1-mini", "gpt-4.1", "gpt-4o-mini", "gpt-4o", "gpt-5-mini", "gpt-5"];
  if (provider === "openrouter") return ["mistralai/mistral-7b-instruct:free", "meta-llama/llama-3.3-70b-instruct:free", "deepseek/deepseek-r1:free"];
  return ["claude-sonnet-4-6", "claude-3-5-sonnet-20241022", "claude-3-5-haiku-20241022", "claude-3-opus-20240229"];
}

function UsersView() {
  const [users, setUsers] = useState<User[]>([]);
  const load = () => api<User[]>("/api/v1/users").then((rows) => setUsers(rows ?? []));
  useEffect(() => { void load(); }, []);
  return (
    <>
      <header className="rr-header"><h1>Users</h1></header>
      <div className="user-list">
        {users.map((user) => (
          <article className="user-card" key={user.id}>
            <div className="user-avatar">{initials(user)}</div>
            <div className="user-body">
              <div className="user-row1"><span className="user-name">{user.fullName || user.email}</span><span className={`user-role role-${user.role}`}>{user.role}</span><span className="status-badge pending">{user.status}</span></div>
              <div className="user-email">{user.email}</div>
              <div className="user-actions">
                <button className="btn-approve" onClick={async () => { await api(`/api/v1/users/${user.id}/approve`, { method: "POST" }); await load(); }}>Approva</button>
                <button className="btn-reject" onClick={async () => { await api(`/api/v1/users/${user.id}/reject`, { method: "POST" }); await load(); }}>Rifiuta</button>
                <button className="btn-role" onClick={async () => { await api(`/api/v1/users/${user.id}/promote`, { method: "POST" }); await load(); }}>Admin</button>
                <button className="btn-remove" onClick={async () => { await api(`/api/v1/users/${user.id}`, { method: "DELETE" }); await load(); }}>Elimina</button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </>
  );
}

function UsageView() {
  const [usage, setUsage] = useState<{ summaries: Array<Record<string, unknown>>; totalCostUsd: number } | null>(null);
  useEffect(() => { api<typeof usage>("/api/v1/usage").then(setUsage); }, []);
  return (
    <>
      <header className="rr-header"><h1>Usage</h1></header>
      <div className="stats-row"><div className="stat-item"><span className="stat-value">${Number(usage?.totalCostUsd ?? 0).toFixed(4)}</span><span className="stat-label">Costo</span></div></div>
      <div className="settings-card"><pre>{JSON.stringify(usage?.summaries ?? [], null, 2)}</pre></div>
    </>
  );
}

function AuditView() {
  const [audit, setAudit] = useState<{ logs: Array<Record<string, unknown>>; totalCount: number } | null>(null);
  useEffect(() => { api<typeof audit>("/api/v1/audit?page=1&pageSize=50").then(setAudit); }, []);
  return (
    <>
      <header className="rr-header"><h1>Audit log</h1><span className="counter-badge">{audit?.totalCount ?? 0}</span></header>
      <div className="audit-table-wrap">
        <table className="audit-table">
          <thead><tr><th>Data</th><th>Entità</th><th>Operazione</th><th>Utente</th></tr></thead>
          <tbody>
            {(audit?.logs ?? []).map((row) => (
              <tr className="audit-row" key={String(row.id)}>
                <td className="audit-ts">{String(row.timestamp_utc ?? "")}</td>
                <td className="audit-entity">{String(row.entity_type ?? "")}</td>
                <td><span className={`audit-op ${String(row.operation ?? "").toLowerCase()}`}>{String(row.operation ?? "")}</span></td>
                <td className="audit-by">{String(row.changed_by ?? "")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

async function api<T>(path: string, init: RequestInit & { quiet?: boolean } = {}): Promise<T | null> {
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      credentials: "include",
      headers: { "content-type": "application/json", ...(init.headers ?? {}) },
      ...init
    });
    if (!response.ok) return null;
    return await response.json() as T;
  } catch {
    return null;
  }
}

function safeJson<T>(value: string, fallback: T): T {
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

function initials(user: User) {
  const value = user.fullName || `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || user.email;
  return value.split(/\s|@/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("");
}

createRoot(document.getElementById("root")!).render(<App />);
